import { supabase } from "@/lib/supabase";

export type CanonicalInternalReviewReadErrorKind =
  | "invalid_request"
  | "authentication_required"
  | "reviewer_scope_required"
  | "target_authority_required"
  | "not_found"
  | "stale_decision"
  | "idempotency_conflict"
  | "prerequisite_blocked"
  | "signing_unavailable"
  | "service_unavailable"
  | "feature_disabled"
  | "unexpected_error";

export class CanonicalInternalReviewReadClientError extends Error {
  constructor(
    public readonly kind: CanonicalInternalReviewReadErrorKind,
    public readonly code: string,
    public readonly status: number | null,
  ) {
    super(code);
    this.name = "CanonicalInternalReviewReadClientError";
  }
}

export type CanonicalInternalReviewFunctionResult = Readonly<{
  data: unknown;
  error: unknown;
}>;

export type CanonicalInternalReviewFunctionInvoker = (
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
) => Promise<CanonicalInternalReviewFunctionResult>;

export function defaultCanonicalInternalReviewInvoker(
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
): Promise<CanonicalInternalReviewFunctionResult> {
  return supabase.functions.invoke(functionName, options);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function assertCanonicalInternalReviewInputKeys(
  value: unknown,
  allowed: readonly string[],
  invalidCode: string,
): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new CanonicalInternalReviewReadClientError(
      "invalid_request",
      invalidCode,
      400,
    );
  }
  const allowedSet = new Set(allowed);
  if (
    Object.keys(value as Record<string, unknown>)
      .some((key) => !allowedSet.has(key))
  ) {
    throw new CanonicalInternalReviewReadClientError(
      "invalid_request",
      invalidCode,
      400,
    );
  }
}

function responseStatus(error: unknown): number | null {
  const transport = asRecord(error);
  const context = asRecord(transport.context);
  const candidate = Number(context.status ?? transport.status);
  return Number.isInteger(candidate) && candidate >= 100 && candidate <= 599
    ? candidate
    : null;
}

function responseCode(
  data: unknown,
  error: unknown,
  prefix: string,
): string {
  const response = asRecord(data);
  for (const value of [response.code, response.error]) {
    if (
      typeof value === "string"
      && (
        value.startsWith(prefix)
        || value === "internal_compliance_review_permission_forbidden"
      )
      && /^[a-z0-9_]+$/.test(value)
    ) {
      return value;
    }
  }
  const transport = asRecord(error);
  if (
    typeof transport.message === "string"
    && (
      transport.message.startsWith(prefix)
      || transport.message === "internal_compliance_review_permission_forbidden"
    )
    && /^[a-z0-9_]+$/.test(transport.message)
  ) {
    return transport.message;
  }
  return `${prefix}internal_error`;
}

export function normalizeCanonicalInternalReviewReadError(
  data: unknown,
  error: unknown,
  prefix: string,
): CanonicalInternalReviewReadClientError {
  const status = responseStatus(error);
  const code = responseCode(data, error, prefix);
  const kind: CanonicalInternalReviewReadErrorKind =
    status === 401 || code.includes("unauthorized")
      ? "authentication_required"
      : code === "internal_compliance_review_permission_forbidden"
        || code.includes("scope_forbidden")
        || code.includes("role_forbidden")
        ? "reviewer_scope_required"
        : status === 403
          ? "target_authority_required"
        : status === 409 && code.includes("stale_decision")
          ? "stale_decision"
          : status === 409 && code.includes("idempotency_conflict")
            ? "idempotency_conflict"
            : status === 409 && code.includes("prerequisite_blocked")
              ? "prerequisite_blocked"
        : status === 404 || code.includes("entity_unavailable")
          ? "not_found"
          : code.includes("preview_unavailable")
            ? "signing_unavailable"
          : status === 500
            || status === 503
            || code.includes("unavailable")
            || code.includes("lookup_failed")
            ? "service_unavailable"
            : status === 400 || code.includes("invalid")
              ? "invalid_request"
              : "unexpected_error";
  return new CanonicalInternalReviewReadClientError(kind, code, status);
}

export function parseCanonicalInternalReviewResult<T>(
  result: CanonicalInternalReviewFunctionResult,
  parser: (value: unknown) => T,
  prefix: string,
): T {
  if (result.error) {
    throw normalizeCanonicalInternalReviewReadError(
      result.data,
      result.error,
      prefix,
    );
  }
  try {
    return parser(result.data);
  } catch {
    throw new CanonicalInternalReviewReadClientError(
      "unexpected_error",
      `${prefix}response_invalid`,
      null,
    );
  }
}

export function canonicalInternalReviewFeatureDisabled(code: string): never {
  throw new CanonicalInternalReviewReadClientError(
    "feature_disabled",
    code,
    null,
  );
}

export const CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES = Object.freeze({
  authentication_required: Object.freeze({
    title: "Session expired",
    description: "Your session has expired. Sign in again to continue.",
  }),
  reviewer_scope_required: Object.freeze({
    title: "Review permission required",
    description:
      "You do not have permission to review compliance for this organization or contractor.",
  }),
  target_authority_required: Object.freeze({
    title: "Review target unavailable",
    description:
      "The requested review target is outside your authorized scope.",
  }),
  not_found: Object.freeze({
    title: "Review unavailable",
    description: "The requested review is unavailable in the selected scope.",
  }),
  stale_decision: Object.freeze({
    title: "Review changed",
    description:
      "Another reviewer changed this decision. Review the latest history before submitting again.",
  }),
  idempotency_conflict: Object.freeze({
    title: "Submission conflict",
    description:
      "The previous submission key conflicts with this request. Refresh and submit again.",
  }),
  prerequisite_blocked: Object.freeze({
    title: "Relationship prerequisites incomplete",
    description:
      "This relationship cannot be approved until its server-derived prerequisites are complete.",
  }),
  signing_unavailable: Object.freeze({
    title: "Preview unavailable",
    description: "The private document preview is temporarily unavailable.",
  }),
  service_unavailable: Object.freeze({
    title: "Review service unavailable",
    description:
      "The canonical review service is temporarily unavailable. No legacy approval fallback was attempted.",
  }),
  invalid_request: Object.freeze({
    title: "Invalid review request",
    description: "The selected review context is invalid.",
  }),
  feature_disabled: Object.freeze({
    title: "Canonical review disabled",
    description: "Canonical internal review is disabled.",
  }),
  unexpected_error: Object.freeze({
    title: "Review unavailable",
    description: "The canonical review response could not be validated safely.",
  }),
} as const);
